import { CommonModule } from '@angular/common';
import {
	AfterContentInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChildren,
	ElementRef,
	HostBinding,
	inject,
	Input,
	NgModule,
	NgZone,
	OnDestroy,
	QueryList,
} from '@angular/core';

import { NavigationSharedModule, PhoneNavigationElementDirective } from '../shared/shared.module';

interface ResizeObserverObj {
	disconnect(): void;
	observe(target: Element, options?: unknown): void;
	unobserve(target: Element): void;
}
// eslint-disable-next-line no-var, @typescript-eslint/no-shadow
declare var ResizeObserver: new (arg0: () => void) => ResizeObserverObj;

@Component({
	// eslint-disable-next-line @angular-eslint/prefer-standalone
	standalone: false,

	selector: 'navigation-phone',
	templateUrl: './phone.component.html',
	styleUrls: ['./phone.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationPhoneComponent<T> implements AfterContentInit, OnDestroy {
	private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
	private changeDetector = inject(ChangeDetectorRef);
	private ngZone = inject(NgZone);

	private _selectedItemId: T;

	@Input()
	public set selectedId(id: T) {
		this._selectedItemId = id;
		this.updateSelectedItem();
	}

	@ContentChildren(PhoneNavigationElementDirective, { descendants: true })
	private navigationElements: QueryList<PhoneNavigationElementDirective<T>> | undefined;

	private selectedItem: PhoneNavigationElementDirective<T> | undefined;
	private _selectedItemLeftPositionPx = 0;
	private _firstTransitionFinished = false;
	private _selectedItemWidthPx = 0;
	private _selectedItemHeightPx = 0;

	private resizeObserver: ResizeObserverObj;

	constructor() {
		this.resizeObserver = new ResizeObserver(() => {
			this.ngZone.run(() => {
				this.updateSelectedItem();
			});
		});
	}

	ngAfterContentInit(): void {
		this.resizeObserver.observe(this.elementRef.nativeElement);
	}

	ngOnDestroy(): void {
		this.resizeObserver.unobserve(this.elementRef.nativeElement);
		this.resizeObserver.disconnect();
	}

	@HostBinding('style.--__selected-item-left-position.px')
	public get selectedItemLeftPositionPx(): number {
		return this._selectedItemLeftPositionPx;
	}

	@HostBinding('style.--__selected-item-width.px')
	public get selectedItemWidthPx(): number {
		return this._selectedItemWidthPx;
	}

	@HostBinding('style.--__selected-item-height.px')
	public get selectedItemHeightPx(): number {
		return this._selectedItemHeightPx;
	}

	@HostBinding('class.__first-transition-finished')
	public get firstTransitionFinished(): boolean {
		return this._firstTransitionFinished;
	}

	public onTransitionEnd(event?: TransitionEvent): void {
		event?.stopPropagation();
		this._firstTransitionFinished = true;
	}

	private updateSelectedItem(): void {
		this.selectedItem?.setAsInactive();
		this.selectedItem = this.navigationElements
			?.toArray()
			.find(({ id }) => id === this._selectedItemId);
		this.selectedItem?.setAsActive();
		this.updateSelectedIndicator();
	}

	private updateSelectedIndicator(): void {
		if (this.selectedItem) {
			const box = this.selectedItem.nativeElement.getBoundingClientRect();
			const itemLeft = box.left;
			const itemWidth = box.width;
			const itemHeight = box.height;

			const selfLeft = this.elementRef.nativeElement.getBoundingClientRect().left;

			this._selectedItemLeftPositionPx = itemLeft - selfLeft;
			this._selectedItemWidthPx = itemWidth;
			this._selectedItemHeightPx = itemHeight;

			this.changeDetector.markForCheck();
		}
	}
}

@NgModule({
	declarations: [NavigationPhoneComponent],
	imports: [CommonModule, NavigationSharedModule],
	exports: [NavigationPhoneComponent],
})
export class NavigationPhoneModule {}
